<template>
  <div>
    <!-- Apple Pay 按钮 -->
    <!-- <button id="apple-pay-button" @click="startApplePaySession">Apple Pay</button> -->
    <apple-pay-button id="apple-pay-button" buttonstyle="black" type="buy" @click="startApplePaySession"></apple-pay-button>
  </div>
</template>

<script>
import { postAppleToken, postAppleDecryptToken } from '@/views/checkout/api/apple-pay'
import { toTransactionResult } from '@/views/checkout/tools'
import { postPaymentLogger } from '@/views/checkout/api'

export default {
  props: {
    order: {
      type: Object,
      default: () => ({})
    },
    getFromData: {
      type: Function,
      default: () => {}
    },
    setErrorTips: {
      type: Function,
      default: () => {}
    }
  },
  data() {
    return {
      orderId: this.$route.params.id,
      orderDetails: this.$props.order,
      MERCHANT_IDENTIFIER: 'merchant.com.wondergate.applepay'
    }
  },
  methods: {
    /**
     * 开始Apple Pay支付流程(包括绑定监听)
     */
    startApplePaySession() {
      // 国家码优先取账单表单中顶部下拉选择的值，其次取订单预设的账单国家/支付国家
      const formData = this.getFromData()
      const countryCode =
        formData?.billingAddress?.country ||
        this.orderDetails?.billing?.country ||
        this.orderDetails?.paymentCountry
      if (!countryCode) {
        this.setErrorTips(
          this.$t('message.common.selectPlaceholder', { field: this.$t('message.checkout.country') })
        )
        postPaymentLogger('Apple Pay countryCode is missing', this.orderId, 'Apple Pay PaymentRequest Error')
        return
      }
      // 定义支付请求参数
      const paymentRequest = {
        countryCode,
        currencyCode: this.orderDetails.currency,
        supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
        merchantCapabilities: ['supports3DS', 'supportsCredit', 'supportsDebit'],
        total: {
          label: this.$t('message.checkout.orderAmount'),
          amount: String(this.orderDetails.price).trim(),
          type: 'final'
        }
        // lineItems: [
        //   {
        //     label: '商品价格',
        //     amount: '89.99'
        //   },
        //   {
        //     label: '税费',
        //     amount: '10.00'
        //   }
        // ],
        // requiredBillingContactFields: ['postalAddress']
        // requiredShippingContactFields: ['postalAddress', 'name', 'phone', 'email']
      }

      // 创建 Apple Pay Session (版本 3)
      const session = new window.ApplePaySession(3, paymentRequest)
      // 设置会话最长等待时间
      session.timeoutInterval = 120

      // 1. 验证商户
      session.onvalidatemerchant = async (event) => {
        try {
          // 1. 验证 validationURL 是否存在
          postPaymentLogger(event, this.orderId, 'Apple Pay Start')
          if (!event.validationURL) {
            postPaymentLogger(event, this.orderId, 'Apple Pay validationURL is missing')
            session.abort()
            return
          }

          // 2. 构建完整的请求
          const requestPayload = {
            url: event.validationURL
          }

          try {
            // 3. 发送商户验证请求到后端
            const response = await postAppleToken(this.orderId, requestPayload)

            // 4. 完成商户验证
            if (!response || typeof response !== 'object') {
              postPaymentLogger(response, this.orderId, 'Apple Pay Create Session Response')
              session.abort()
              return
            }
            const authResponse = { ...response }
            delete authResponse.status
            session.completeMerchantValidation(authResponse)
          } catch (e) {
            this.setErrorTips(JSON.stringify(e.message))
            postPaymentLogger(e, this.orderId, 'Apple Pay Create Session Error')
            session.abort()
          }
        } catch (error) {
          this.setErrorTips(JSON.stringify(error.message))
          postPaymentLogger(error, this.orderId, 'Apple Pay Merchant Validation Error')
          session.abort()
        }
      }

      // 2.1 处理支付方式选择
      session.onpaymentmethodselected = (event) => {
        const update = {
          newTotal: paymentRequest.total
        }
        session.completePaymentMethodSelection(update)
      }

      // 2.2 处理配送方式选择
      session.onshippingmethodselected = (event) => {
        const update = {
          newTotal: paymentRequest.total
        }
        session.completeShippingMethodSelection(update)
      }

      // 3. 处理配送地址选择
      session.onshippingcontactselected = (event) => {
        const update = {
          newTotal: paymentRequest.total
        }
        session.completeShippingContactSelection(update)
      }

      // 4. 获取支付授权（最关键的步骤）
      session.onpaymentauthorized = async (event) => {
        const payment = event.payment
        try {
          // 发送商户验证请求到后端
          const response = await postAppleDecryptToken(this.orderId, {
            paymentToken: payment.token,
            billingContact: payment.billingContact,
            shippingContact: payment.shippingContact,
            ...this.getFromData()
          })
          if (+response.status === 100) {
            // 通知 Apple Pay 支付成功
            session.completePayment(window.ApplePaySession.STATUS_SUCCESS)
            postPaymentLogger('Apple Pay SUCCESS', this.orderId, 'Apple Pay STATUS')
          } else if (+response.status === 301) {
            session.completePayment(window.ApplePaySession.STATUS_SUCCESS)
            window.location.href = response.redirectUrl
            return
          } else {
            session.completePayment(window.ApplePaySession.STATUS_FAILURE)
            postPaymentLogger('Apple Pay FAILURE', this.orderId, 'Apple Pay STATUS')
          }
          this.$nextTick(() => {
            toTransactionResult(this, response, this.orderId)
          })
        } catch (error) {
          this.setErrorTips(JSON.stringify(error.message))
          postPaymentLogger(error, this.orderId, 'Apple Pay Decrypt Token Error')
        }
      }

      // 5. 处理取消
      session.oncancel = async (event) => {
        postPaymentLogger(event, this.orderId, 'Apple Pay Cancel')
        const _this = this
        const promise = window.ApplePaySession?.applePayCapabilities(this.MERCHANT_IDENTIFIER)
        promise
          .then(function (capabilities) {
            // Check if the person has an active payment credential provisioned in Wallet.
            switch (capabilities.paymentCredentialStatus) {
              case 'paymentCredentialsAvailable':
                // 显示 Apple Pay 按钮，并将 Apple Pay 作为主要支付方式。
                return
              case 'paymentCredentialStatusUnknown':
                // 显示一个 Apple Pay 按钮，并提供 Apple Pay 作为支付选项。 这个是因为无法确定是不是safari浏览器
                _this.setErrorTips(_this.$t('message.checkout.notSupportPaymentTip', { type: 'Apple Pay' }))
                postPaymentLogger('paymentCredentialStatusUnknown', _this.orderId, 'Apple Pay Card Unknown')
                break
              case 'paymentCredentialsUnavailable':
                // 考虑显示一个 Apple Pay 按钮。 这个可能是因为没有绑卡
                _this.setErrorTips(_this.$t('message.checkout.addValidPaymentCard'))
                postPaymentLogger('paymentCredentialsUnavailable', _this.orderId, 'Apple Pay Card Unavailable')
                break
              case 'applePayUnsupported':
                // 不要显示 Apple Pay 按钮或提供 Apple Pay 服务。
                _this.setErrorTips(_this.$t('message.checkout.notSupportPaymentTip', { type: 'Apple Pay' }))
                postPaymentLogger('applePayUnsupported', _this.orderId, 'Apple Pay Card Unsupported')
                break
            }
          })
          .catch(async () => {
            try {
              // 检测当前设备是否支持 Apple Pay 功能
              const canMakePayments = await window.ApplePaySession.canMakePayments()
              // 用于检查设备是否支持 Apple Pay 并且 用户已添加至少一张有效支付卡。
              const canMakePaymentsWithActiveCard = await window.ApplePaySession.canMakePaymentsWithActiveCard(
                _this.MERCHANT_IDENTIFIER
              )
              if (canMakePayments && canMakePaymentsWithActiveCard) {
              } else if (!canMakePayments) {
                _this.setErrorTips(_this.$t('message.checkout.notSupportPaymentTip', { type: 'Apple Pay' }))
                postPaymentLogger('The current environment does not support Apple Pay', _this.orderId, 'Apple Pay Card Error')
              } else {
                _this.setErrorTips(_this.$t('message.checkout.addValidPaymentCard'))
                postPaymentLogger('No valid payment card has been added', _this.orderId, 'Apple Pay Card Error')
              }
            } catch (error) {
              postPaymentLogger(error, _this.orderId, 'Apple Pay Cancel Error')
            }
          })

        // window.location.reload()
      }

      // 6. Apple Pay 错误
      session.onerror = (error) => {
        postPaymentLogger(error, this.orderId, 'Apple Pay Error')
      }
      // 启动 Session
      session.begin()
    }
  }
  // async mounted() {
  // }
}
</script>

<style lang="less" scoped>
@import '~@/styles/custom.less';

#apple-pay-button {
  .pay-button();
}

.log-section {
  margin-top: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  max-height: 400px;
  overflow-y: auto;
}

.log-section h3 {
  font-size: 16px;
  margin-bottom: 15px;
  color: #333;
}

.log-entry {
  background: white;
  padding: 12px;
  margin-bottom: 10px;
  border-radius: 6px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  border-left: 3px solid #667eea;
}

.log-entry.error {
  border-left-color: #dc3545;
}

.log-entry.success {
  border-left-color: #28a745;
}

.log-timestamp {
  color: #999;
  font-size: 11px;
}
</style>
